// Operaciones · Torniquete · Dobles entradas
//
// La alerta del QR compartido (27-10-2026): una persona entró con su cédula y 15
// segundos después un amigo entró por el otro torniquete con el QR de la misma cuenta.
// El equipo no distingue cédula de QR ni sabe quién gira, así que el sistema no puede
// impedirlo; lo que sí puede es mostrarle al guardia, casi en el momento, que el mismo
// RUT abrió dos veces.
//
// Por defecto se muestran solo las del OTRO torniquete: en el mismo torniquete casi
// siempre es alguien que no alcanzó a girar y volvió a pasar la cédula (190 el 26-10,
// contra 12 entre torniquetes distintos).

import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Space, Typography, Button, Switch, Empty } from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { AccesoAdmin } from '../../../../service/Acceso';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';

const { Text, Paragraph } = Typography;

// Cada cuánto se refresca sola: el job del torniquete mira los eventos cada minuto.
const REFRESCO_MS = 60 * 1000;

const hora = (fechaHora) => (fechaHora || '').slice(11, 19);

export default function TorniqueteDobles() {
  const notify = useNotify();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [todas, setTodas] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setDatos(await AccesoAdmin.doblesEntradas());
    } catch (e) {
      notify?.error?.(mensajeDeError(e));
    } finally {
      setCargando(false);
    }
  }, [notify]);

  useEffect(() => {
    cargar();
    const id = setInterval(cargar, REFRESCO_MS);
    return () => clearInterval(id);
  }, [cargar]);

  const dobles = (datos?.dobles || []).filter(d => todas || d.otroTorniquete);

  return (
    <Card
      size="small"
      title={
        <span>
          <WarningOutlined /> Dobles entradas de hoy
          {datos?.totales?.otroTorniquete > 0 && (
            <Tag color="red" style={{ marginLeft: 8 }}>{datos.totales.otroTorniquete} por el otro torniquete</Tag>
          )}
        </span>
      }
      extra={
        <Space>
          <Space size={4}>
            <Switch size="small" checked={todas} onChange={setTodas} />
            <Text style={{ fontSize: 12 }}>Incluir mismo torniquete ({datos?.totales?.dobles ?? 0})</Text>
          </Space>
          <Button size="small" icon={<ReloadOutlined />} loading={cargando} onClick={cargar}>
            Actualizar
          </Button>
        </Space>
      }
    >
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        El mismo RUT abrió dos veces una entrada en menos de 2 minutos. Puede ser alguien
        que no alcanzó a girar y volvió a intentar, o dos personas con la misma cédula o el
        mismo QR: el torniquete no sabe quién pasa. Se actualiza sola cada minuto.
      </Paragraph>
      {datos?.sinPuertasDeEntrada ? (
        <Empty image={null} description="No hay puertas marcadas como entrada (pestaña Puertas)" />
      ) : (
        <Table
          size="small"
          rowKey={(r) => `${r.pin}-${r.primera}`}
          dataSource={dobles}
          loading={cargando && !datos}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{ emptyText: 'Sin dobles entradas' }}
          columns={[
            { title: 'Hora', width: 150, render: (_, r) => `${hora(r.primera)} → ${hora(r.ultima)}` },
            { title: 'RUT', dataIndex: 'pin', width: 110, render: (v) => <Text code>{v}</Text> },
            { title: 'Persona', dataIndex: 'nombre', render: (v) => v || <Text type="secondary">sin nombre</Text> },
            { title: 'Aperturas', dataIndex: 'aperturas', width: 90 },
            {
              title: 'Puertas',
              render: (_, r) => (
                <Space size={4} wrap>
                  {r.otroTorniquete
                    ? <Tag color="red">otro torniquete</Tag>
                    : <Tag>mismo torniquete</Tag>}
                  <Text type="secondary" style={{ fontSize: 12 }}>{(r.puertas || []).join(' · ')}</Text>
                </Space>
              ),
            },
          ]}
        />
      )}
    </Card>
  );
}
